import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { RefreshCw, LogOut, Bell, Check, X } from "lucide-react";
import { fetchAdminProducts, updateProductStock } from "../api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-[#141210] border border-[#2c2822] rounded-lg p-6">
    <div className="flex items-center gap-3 mb-2">
      <Icon size={18} className="text-[#c9a24b]" />
      <span className="text-[#a29a8c] text-xs tracking-[0.15em] uppercase">{label}</span>
    </div>
    <p className="font-display text-4xl text-[#f4efe4]">{value}</p>
  </div>
);

const AdminInventory = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchAdminProducts();
      setProducts(data);
    } catch (e) {
      // errors surface globally
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const toggleStock = async (p) => {
    const next = !p.inStock;
    setBusy(p.slug);
    setProducts((prev) => prev.map((x) => (x.slug === p.slug ? { ...x, inStock: next } : x)));
    try {
      const res = await updateProductStock(p.slug, next);
      if (next && res?.notified > 0) {
        toast.success("Marked in stock", {
          description: res.email_enabled
            ? `Notified ${res.notified} waiting customer${res.notified === 1 ? "" : "s"} by email.`
            : `${res.notified} customer(s) were waiting, but email isn't set up yet.`,
        });
      } else {
        toast.success(next ? "Marked in stock" : "Marked sold out", { description: p.name });
      }
      load();
    } catch (e) {
      setProducts((prev) => prev.map((x) => (x.slug === p.slug ? { ...x, inStock: p.inStock } : x)));
      toast.error("Update failed", { description: "Please try again." });
    } finally {
      setBusy(null);
    }
  };

  const inStockCount = products.filter((p) => p.inStock).length;
  const soldOutCount = products.filter((p) => !p.inStock).length;
  const waitingTotal = products.reduce((s, p) => s + (p.waiting || 0), 0);

  return (
    <div className="max-w-[1280px] mx-auto px-6 py-16" data-testid="admin-inventory">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <p className="text-[#c9a24b] text-sm tracking-[0.3em] uppercase mb-2">Admin</p>
          <h1 className="font-display text-4xl text-[#f4efe4]">Inventory</h1>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={load} className="btn-outline-gold px-4 py-2 rounded-full text-xs tracking-[0.15em] uppercase inline-flex items-center gap-2" data-testid="inventory-refresh-btn">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button onClick={doLogout} className="text-[#a29a8c] hover:text-[#c9a24b] transition-colors inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em]">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </div>

      <div className="flex gap-4 mb-10 text-xs tracking-[0.15em] uppercase">
        <Link to="/admin/orders" className="text-[#a29a8c] hover:text-[#c9a24b] pb-1">Orders</Link>
        <Link to="/admin/inventory" className="text-[#c9a24b] border-b border-[#c9a24b] pb-1">Inventory</Link>
        <Link to="/admin/reviews" className="text-[#a29a8c] hover:text-[#c9a24b] pb-1">Reviews</Link>
        <Link to="/admin/visitors" className="text-[#a29a8c] hover:text-[#c9a24b] pb-1">Visitors</Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
        <StatCard icon={Check} label="In Stock" value={inStockCount} />
        <StatCard icon={X} label="Sold Out" value={soldOutCount} />
        <StatCard icon={Bell} label="Awaiting Restock" value={waitingTotal} />
      </div>

      <div className="bg-[#141210] border border-[#2c2822] rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-[#2c2822]">
          <h2 className="font-display text-xl text-[#f4efe4]">Products</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[#6b6459] text-xs tracking-[0.12em] uppercase">
                <th className="text-left font-medium px-6 py-3">Product</th>
                <th className="text-left font-medium px-6 py-3">Price</th>
                <th className="text-left font-medium px-6 py-3">Waiting</th>
                <th className="text-left font-medium px-6 py-3">Status</th>
                <th className="text-right font-medium px-6 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr><td colSpan={5} className="px-6 py-10 text-center text-[#a29a8c]">No products.</td></tr>
              ) : (
                products.map((p) => (
                  <tr key={p.slug} className="border-t border-[#2c2822] text-[#c8c0b2] hover:bg-[#1b1815] transition-colors" data-testid={`inventory-row-${p.slug}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-md overflow-hidden bg-gradient-to-b from-[#f6f1e7] to-[#e4dccb] shrink-0">
                          {p.image && <img src={p.image} alt="" className="w-full h-full object-contain p-1" />}
                        </div>
                        <div>
                          <div className="text-[#f4efe4]">{p.name}</div>
                          <div className="text-[#6b6459] text-xs">{p.category}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-[#c9a24b]">${Number(p.price || 0).toFixed(2)}</td>
                    <td className="px-6 py-4">
                      {p.waiting > 0 ? (
                        <span className="inline-flex items-center gap-1.5 text-amber-400">
                          <Bell size={13} /> {p.waiting}
                        </span>
                      ) : (
                        <span className="text-[#6b6459]">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-block border rounded-full px-3 py-1 text-xs ${p.inStock ? "text-green-400 border-green-500/40" : "text-red-400 border-red-500/40"}`} data-testid={`inventory-status-${p.slug}`}>
                        {p.inStock ? "In Stock" : "Sold Out"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => toggleStock(p)}
                        disabled={busy === p.slug}
                        data-testid={`inventory-toggle-${p.slug}`}
                        className={`px-4 py-2 rounded-full text-xs tracking-[0.12em] uppercase transition-colors disabled:opacity-60 ${
                          p.inStock
                            ? "border border-[#2c2822] text-[#a29a8c] hover:border-red-500/50 hover:text-red-400"
                            : "btn-gold"
                        }`}
                      >
                        {busy === p.slug ? "Saving…" : p.inStock ? "Mark Sold Out" : "Mark In Stock"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-[#6b6459] text-xs mt-4">Private page — bookmark: /admin/inventory</p>
    </div>
  );
};

export default AdminInventory;
